/* ============================================================================
   PREDICTIVE SEARCH — Trade's controller, with SVNTY's corrections.
   SVNTY, 2026-09-19, dev1.0.

   Kept from Trade: the custom element, the combobox contract, arrow/Enter
   navigation over role="option" rows, the per-instance result cache, the
   aria-live status. Changed, each for a reason a predecessor paid for:

   1. MIN_CHARS = 2. One character asks Shopify for the whole catalogue and
      answers with noise; the sheet holds its curated page until the second key.
   2. ABORT IN FLIGHT. Trade builds ONE AbortController in the constructor and
      only ever aborts it on a form reset, so on a slow connection two answers
      race and the older one can render last. Every request now cancels its
      predecessor (fetch rejects with code 20 / AbortError, which is not an error).
   3. THE RESOURCE CONTRACT IS EXPLICIT: products, collections, pages and query
      completions, sold-out products last. Trade sent no `resources[*]` at all
      and inherited whatever the endpoint defaulted to.
   4. THE TERM IS NOT A REGULAR EXPRESSION. Trade rebuilt the "search for" label
      with `new RegExp(previousTerm, 'g')` — a term containing `(`, `[`, `*` or
      `+` throws a SyntaxError inside the input handler and kills the typeahead,
      and `.match()` returning null throws on `.length`. The label is now written,
      not pattern-matched.
   5. NO JS HEIGHT. Trade measured `window.innerHeight` minus `.section-header`'s
      bounding box on every open and wrote it as an inline max-height: it throws
      where no header section exists, and on iOS `innerHeight` changes on every
      URL-bar collapse (LESSONS 5.1 / TRAPS IOS-07). assets/svnty-search.css owns
      the panel's height, in svh.

   ══ ROUND 2 ══

   6. THE ARABIC FOLD IS A FALLBACK, NOT A FILTER (R2-12). `getQuery()` now
      returns the SAFE normalisation — tashkeel and tatweel stripped, every
      LETTER as the shopper typed it — because folding the needle against an
      unfolded index destroyed real result sets (`أبيض` 7 options -> `ابيض` 0).
      When the raw term comes back with NO options and its folded spelling
      differs, exactly one extra request goes out with the folded spelling and
      its answer is rendered. Raw first, folded only if raw found nothing, once.

   7. IDS ARE SCOPED PER INSTANCE (R2-16). /search carries TWO instances — the
      header sheet and the page field — and both were served markup containing
      `id="predictive-search-results"` and `id="predictive-search-option-product-N"`.
      After the sheet had been used, the page field's `aria-owns`,
      `aria-controls` and `aria-activedescendant` all resolved to the HEADER's
      listbox, because `getElementById` answers with the first match in the
      document. Every injected id is prefixed with this instance's input id, and
      the input's own references are written to match. The section template stays
      instance-agnostic, which is what the Section Rendering API requires.

   8. `aria-activedescendant` IS REMOVED, NOT EMPTIED (R2-49). `''` is an invalid
      IDREF, not "no active descendant".

   9. THE KEYUP HANDLER MINDS ITS BUSINESS (R2-49). Trade called
      `event.preventDefault()` on EVERY keyup and cleared the field whenever the
      normalised query was empty — so a leading space (how a shopper starts
      " polo") was erased as it was typed. preventDefault now belongs to the keys
      that navigate, and the field is only cleared when the field itself is empty.

  10. A FAILED FETCH DOES NOT BECOME AN UNHANDLED REJECTION (R2-49). The panel
      closes and `assets/svnty-search.js`'s watchdog says so; re-throwing inside
      a `.catch` only produced a console error nobody reads.
   ============================================================================ */
class PredictiveSearch extends SearchForm {
  static MIN_CHARS = 2;
  static RESOURCE_TYPES = 'product,collection,page,query';
  // ══ ROUND 3 (S-1): `resources[limit]` IS PER TYPE ONCE YOU SEND THE RIGHT KEY ══
  // Round 2 measured `resources[limit]` as a TOTAL and recorded that `limit_scope` "has no
  // effect". Both halves of that note were true of the key it tested —
  // `resources[options][limit_scope]`, which Shopify ignores — and false of the real one.
  // Re-measured on the dev proxy, 2026-09-19, counting `role="option"` rows:
  //
  //     q=shirt, no scope                      -> 4 products · 3 collections · 3 suggestions
  //     q=shirt, resources[limit_scope]=each   -> 6 products · 3 collections · 3 suggestions
  //     q=shirt, resources[options][limit_scope]=each -> 4 products (ignored, as round 2 saw)
  //     q=polo  -> 6 products either way (Shopify had already allocated 6)
  //
  // So without the key a sheet built for six product rows was served four whenever the term
  // also matched collections and query completions — the shopper lost two products to make
  // room for chrome. `limit_scope=each` makes `resources[limit]` a PER-TYPE ceiling, which is
  // what sections/predictive-search.liquid's caps (4 queries · 6 products · 3 collections ·
  // 2 pages · 2 articles) always assumed. The section's caps stay the authority on the shape:
  // they are lower than 10, so they still clamp, and the endpoint no longer decides the mix.
  static RESOURCE_LIMIT = 10;
  static RESOURCE_LIMIT_SCOPE = 'each';

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.foldTried = {};

    // R2-16: point the combobox at THIS instance's listbox before anything is injected.
    if (this.input) {
      const owned = `${this.idScope}-predictive-search-results`;
      if (this.input.hasAttribute('aria-owns')) this.input.setAttribute('aria-owns', owned);
      if (this.input.hasAttribute('aria-controls')) this.input.setAttribute('aria-controls', owned);
    }

    this.setupEventListeners();
  }

  // One stable prefix per instance, derived from the input's own id (`Search-In-Modal`,
  // `Search-In-Template`), which Liquid already guarantees is unique per render.
  get idScope() {
    if (!this._idScope) {
      const seed = (this.input && this.input.id) || 'predictive';
      this._idScope = `svnty-${String(seed).replace(/[^A-Za-z0-9_-]/g, '')}`;
    }
    return this._idScope;
  }

  // Runs on every injection: the section template cannot know which instance asked for it.
  scopeInjectedIds() {
    if (!this.predictiveSearchResults) return;
    const scope = this.idScope;
    this.predictiveSearchResults.querySelectorAll('[id^="predictive-search"]').forEach((node) => {
      node.id = `${scope}-${node.id}`;
    });
    this.predictiveSearchResults
      .querySelectorAll('[aria-labelledby^="predictive-search"]')
      .forEach((node) => {
        node.setAttribute('aria-labelledby', `${scope}-${node.getAttribute('aria-labelledby')}`);
      });
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  // R2-12: the SAFE normalisation. `بولو` typed with tashkeel or a kashida reaches Shopify
  // without them; every letter reaches Shopify exactly as it was typed.
  normalizeTerm(value) {
    const safe = window.svntySearch && window.svntySearch.normalize;
    return typeof safe === 'function' ? safe(value) : String(value || '').trim();
  }

  // R2-12: the FALLBACK SPELLINGS, tried only after the raw term found nothing. The list and the
  // measurement behind its order live in assets/svnty-search.js (`foldCandidates`) — on this store's
  // index the hamza is what the catalogue stores and what the shopper omits, so the useful candidate
  // is the RESTORATION (`ابيض` -> `أبيض`, 0 -> 7 options), not the fold.
  foldCandidates(value) {
    const list = window.svntySearch && window.svntySearch.foldCandidates;
    return typeof list === 'function' ? list(value) : [];
  }

  getQuery() {
    return this.normalizeTerm(this.input.value);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again.
      // R2-16: by CLASS, not by id — the id is scoped per instance now.
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // R2-49: only clear the FIELD when the field is empty. A leading space normalises to ''
      // but the shopper is mid-word, and `close(true)` would wipe what they typed.
      this.close(this.input.value.length === 0);
      return;
    }

    // SVNTY: below the floor the sheet keeps its curated page; the term stays as typed.
    if (this.searchTerm.length < PredictiveSearch.MIN_CHARS) {
      this.close(false);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else if (currentSearchTerm.length >= PredictiveSearch.MIN_CHARS) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  // R2-49: preventDefault belongs to the keys that navigate the listbox. On every other key it
  // suppressed the input's own default behaviour for no reason at all.
  onKeyup(event) {
    if (!this.input.value.length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    /* S-11 — ONE MATCH, ONE STOP, AND IT HAS TO BE keydown.
       When the answer holds exactly one resource the section stamps
       `[data-single-result-url]` and Enter goes straight to it instead of opening a results
       page built to show one card (the sibling: assets/predictive-search.js:219-224 +
       sections/predictive-search.liquid:130-145).
       THE EVENT MATTERS. Enter in a form with a submit button triggers IMPLICIT SUBMISSION on
       keydown — the navigation to /search?q=... starts before `keyup` ever fires, so the first
       version of this, written into `onKeyup` beside `selectOption()`, could never have run.
       (Trade's own `selectOption()` on keyup works only because `onFormSubmit` cancels the
       submission whenever a row is selected, which leaves keyup a live page to act on.)
       An arrowed-to row always wins: the shopper has already chosen.
       The submit BUTTON is untouched - pressing the magnifier means "show me the page". */
    if (event.code !== 'Enter' && event.key !== 'Enter') return;
    if (this.querySelector('[aria-selected="true"]')) return;
    const single = this.singleResultUrl();
    if (!single) return;
    event.preventDefault();
    window.location.href = single;
  }

  // SVNTY: written, never pattern-matched. The element holds the quoted term alone.
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement) return;
    searchForTextElement.textContent = `“${newTerm}”`;
    const rtl = window.svntySearch && window.svntySearch.isArabic && window.svntySearch.isArabic(newTerm);
    searchForTextElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    activeElement.scrollIntoView({ block: 'nearest' });
  }

  singleResultUrl() {
    const node = this.querySelector('[data-single-result-url]');
    return (node && node.getAttribute('data-single-result-url')) || '';
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  // Returns a promise so the fold-candidate chain can wait for the render before deciding whether
  // to try the next spelling. Every path resolves — a failure is handled here, never re-thrown.
  getSearchResults(searchTerm, isFoldFallback = false) {
    const queryKey = encodeURIComponent(searchTerm.toLowerCase());
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (!isFoldFallback) this.maybeFoldFallback(searchTerm);
      return Promise.resolve();
    }

    // SVNTY: cancel the request in flight so the last answer is always the last word.
    this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': PredictiveSearch.RESOURCE_TYPES,
      'resources[limit]': PredictiveSearch.RESOURCE_LIMIT,
      'resources[limit_scope]': PredictiveSearch.RESOURCE_LIMIT_SCOPE,
      'resources[options][unavailable_products]': 'last',
    });

    return fetch(`${routes.predictive_search_url}?${params}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          /* R3-29: the watchdog cannot see the status, so the status is left where it can.
             429 is a bot-check, not slowness; anything else that answered is a server fault. */
          this.dataset.svntySearchFail = response.status === 429 ? 'busy' : 'down';
          this.close();
          throw error;
        }

        delete this.dataset.svntySearchFail;

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        if (!isFoldFallback) this.maybeFoldFallback(searchTerm);
      })
      .catch((error) => {
        // Code 20 / AbortError means this request was superseded, not that it failed.
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        // R2-49: closing IS the handling. The watchdog in assets/svnty-search.js turns the
        // missing answer into a sentence and a retry; re-throwing here only produced an
        // unhandled rejection on every genuine failure.
        // R3-29: a throw with no status (a dropped connection) is "not answering", not "slow".
        if (!this.dataset.svntySearchFail) this.dataset.svntySearchFail = 'down';
        this.close();
      });
  }

  /* R2-12: AT MOST TWO EXTRA REQUESTS, AND ONLY OVER AN EMPTY PANEL. If the raw term rendered no
     options at all, the candidate spellings are tried in order and the first one that comes back
     with rows is what the shopper sees. Guarded per term, so holding a key cannot turn this into a
     loop, and never entered FROM a fallback answer (`isFoldFallback`), so the depth is exactly one.
     Two is the cap because a third request on Egyptian mobile data costs more than the third
     candidate is worth — the results page, which has a whole page to spend, tries all three. */
  static MAX_FOLD_TRIES = 2;

  maybeFoldFallback(searchTerm) {
    if (!this.predictiveSearchResults) return;
    if (this.predictiveSearchResults.querySelector('[role="option"]')) return;
    if (this.foldTried[searchTerm]) return;
    this.foldTried[searchTerm] = true;
    const candidates = this.foldCandidates(searchTerm).slice(0, PredictiveSearch.MAX_FOLD_TRIES);
    if (!candidates.length) return;
    this.runFoldCandidate(searchTerm, candidates, 0);
  }

  runFoldCandidate(rawTerm, candidates, index) {
    if (index >= candidates.length) return;
    // The shopper has typed on: this answer is no longer about the term in the field.
    if (this.searchTerm !== rawTerm) return;
    this.getSearchResults(candidates[index], true).then(() => {
      if (this.searchTerm !== rawTerm) return;
      if (this.predictiveSearchResults.querySelector('[role="option"]')) return;
      this.runFoldCandidate(rawTerm, candidates, index + 1);
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.scopeInjectedIds();
    this.setAttribute('results', true);

    // S-3: A NEW ANSWER STARTS AT THE TOP. The panel is re-rendered IN PLACE, so after
    // scrolling the answer for `sh` the shopper who types `shi` was left mid-list — or, when
    // the new answer is shorter, below its last row looking at nothing. The sibling resets both of
    // its scrollers on every render (assets/predictive-search.js:307-312, called at :339);
    // the two here are the sheet's single body scroller and, on /search, the panel itself.
    this.resetScrollPositions();

    this.setLiveRegionResults();
    this.open();
    this.svntyOfferAll();
  }

  /* P17-19 (the eye pass's P3-7): THE ROAD TO THE FULL SEARCH, VERIFIED BEFORE IT IS OFFERED. The empty state's
     "Search for <term>" (R3-12: the full search also reads page bodies) ships hidden; the count of the page it opens
     (sections/svnty-search-count.liquid) decides whether it shows, so a plain miss never leads to a second empty page.
     One small request per address, kept for the page's life; the reveal waits out a press in progress (THE iOS TAP LAW:
     nothing moves between a press and its click). */
  svntyOfferAll() {
    const btn = this.predictiveSearchResults && this.predictiveSearchResults.querySelector('[data-svnty-search-all]');
    if (!btn || !btn.hidden) return;
    const href = btn.getAttribute('href') || '';
    if (!href || typeof fetch !== 'function') return;
    const cache = (PredictiveSearch.svntyAllCache = PredictiveSearch.svntyAllCache || {});
    const reveal = (n) => {
      if (!(n > 0) || !btn.isConnected) return;
      const wait = 600 - (performance.now() - (PredictiveSearch.svntyLastDown || -1e9));
      if (wait > 0) {
        setTimeout(() => reveal(n), wait);
        return;
      }
      btn.hidden = false;
    };
    if (!cache[href]) {
      cache[href] = fetch(href + (href.indexOf('?') > -1 ? '&' : '?') + 'section_id=svnty-search-count', { credentials: 'same-origin' })
        /* P28 lane E (launch audit async P2-1): a refused answer (the store's 429, a 5xx) is not a count of 0. It throws, so the catch
           forgets it and the next keystroke on this term asks again; one quiet retry first on 429/5xx (Retry-After, capped 4 s). */
        .then((response) => {
          if (response.status === 429 || response.status >= 500) {
            const after = parseFloat(response.headers.get('Retry-After'));
            return new Promise((resolve) => setTimeout(resolve, Math.min(after > 0 ? after * 1000 : 900, 4000)))
              .then(() => fetch(href + (href.indexOf('?') > -1 ? '&' : '?') + 'section_id=svnty-search-count', { credentials: 'same-origin' }));
          }
          return response;
        })
        .then((response) => {
          if (!response.ok) throw new Error('count ' + response.status);
          return response.text();
        })
        .then((text) => {
          const m = /data-svnty-search-count="(\d+)"/.exec(text || '');
          if (!m) throw new Error('count shape');
          return parseInt(m[1], 10);
        })
        .catch(() => {
          delete cache[href];
          return 0;
        });
    }
    cache[href].then(reveal);
  }

  // Both scrollers, because the sheet and the page scroll at different levels:
  // `.svnty-search__body` is the sheet's ONE scroller (assets/svnty-search.css §1) and
  // `.svnty-search-page .predictive-search` is the page's own dropdown (§ the page panel).
  // In a rAF, so it runs after layout has taken the new markup's height into account.
  resetScrollPositions() {
    const scrollers = [this.predictiveSearchResults, this.closest('.svnty-search__shell')?.querySelector('.svnty-search__body')];
    requestAnimationFrame(() => {
      scrollers.forEach((node) => {
        if (node && node.scrollTop) node.scrollTop = 0;
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    // R2-49: no active descendant is the ABSENCE of the attribute. '' is a broken IDREF.
    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
  }

  /* S-4 — THE SHEET OPENS ON ITS CURATED PAGE, EVERY TIME.
     `snippets/header-search.liquid` states that contract in its own header, and the sheet did
     not keep it: `details-modal.js` only toggles `<details>`, so a sheet closed mid-search
     reopened holding the previous term, and on a mouse `onFocus` immediately re-opened the
     previous answer over the curated page. The sibling resets on its dialog's close event
     (assets/predictive-search.js:107-111). The reset is deliberately NOT `onFormReset`: that
     path belongs to the shopper pressing CLEAR and fires Trade's own listeners.
     Called by assets/svnty-search.js when the sheet's <details> closes. */
  resetForClose() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.foldTried = {};
    this.input.value = '';
    this.input.removeAttribute('dir');
    this.input.style.textAlign = '';
    const reset = this.input.form && this.input.form.querySelector('button[type="reset"]');
    if (reset) {
      reset.classList.add('hidden');
      reset.disabled = true;
    }
    this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    this.closeResults(true);
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);

/* P17-19: the last press anywhere, read by svntyOfferAll's wait (capture and passive: it never blocks or delays a gesture) */
document.addEventListener(
  'pointerdown',
  () => {
    PredictiveSearch.svntyLastDown = performance.now();
  },
  { capture: true, passive: true }
);
